import React from 'react';
import { NewTripRequest } from '../../types';

interface DailyLodgingState {
  [key: string]: { status: 'has' | 'none' | 'need', comment: string };
}

interface StepProps {
  formData: NewTripRequest;
  setFormData: React.Dispatch<React.SetStateAction<NewTripRequest>>;
  dailyLodging: DailyLodgingState;
  setDailyLodging: React.Dispatch<React.SetStateAction<DailyLodgingState>>;
  lodgingInput: string;
  setLodgingInput: React.Dispatch<React.SetStateAction<string>>;
}

const StepLodging: React.FC<StepProps> = ({ 
  formData, 
  setFormData, 
  dailyLodging, 
  setDailyLodging,
  lodgingInput,
  setLodgingInput
}) => {

  const getTripDays = () => {
    if (!formData.startDate || !formData.endDate) return [];
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d).toISOString().split('T')[0]);
    }
    return days;
  };

  const handleDailyLodgingChange = (date: string, status: 'has' | 'none' | 'need', comment: string = '') => {
    setDailyLodging(prev => ({
      ...prev,
      [date]: { status, comment }
    }));
  };

  const handleLodgingStatusChange = (date: string, status: 'has' | 'none' | 'need') => {
    const tripDays = getTripDays();
    const currentIndex = tripDays.indexOf(date);
    
    // Auto-fill logic
    if (status === 'has' && currentIndex > 0) {
      const previousDate = tripDays[currentIndex - 1];
      const previousLodging = dailyLodging[previousDate];
      if (previousLodging && previousLodging.status === 'has' && previousLodging.comment.trim()) {
        setDailyLodging(prev => ({
          ...prev,
          [date]: { status, comment: previousLodging.comment }
        }));
        return;
      }
    }
    
    setDailyLodging(prev => ({
      ...prev,
      [date]: { status, comment: '' }
    }));
  };

  const accommodationTypes = [
    { value: 'hotel', label: 'Hotel', icon: '🏨' },
    { value: 'airbnb', label: 'Airbnb / Rental', icon: '🏡' },
    { value: 'hostel', label: 'Hostel', icon: '🛏️' },
    { value: 'resort', label: 'Resort', icon: '🏖️' },
    { value: 'guesthouse', label: 'Guesthouse', icon: '🏠' },
    { value: 'camping', label: 'Camping', icon: '⛺' }
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Accommodation Type */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Preferred Accommodation Type</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {accommodationTypes.map((type) => (
                <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, accommodationType: type.value }))}
                    className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${
                        formData.accommodationType === type.value
                        ? 'border-primary-500 bg-primary-50 text-primary-900 shadow-md scale-[1.02]'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-600'
                    }`}
                >
                    <span className="text-3xl mb-2">{type.icon}</span>
                    <span className="font-medium">{type.label}</span>
                </button>
            ))}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4"></div>

      {/* General Preferences */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">General Preferences</h3>
        <textarea
            value={lodgingInput}
            onChange={(e) => setLodgingInput(e.target.value)}
            rows={3}
            className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-3 px-4 bg-gray-50"
            placeholder="e.g. Prefer hotels with breakfast included, family-friendly amenities, near city center..."
        />
      </div>

      <div className="border-t border-gray-100 pt-4"></div>

      {/* Daily Status (Only if dates are selected) */}
      {formData.startDate && formData.endDate && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Daily Lodging Status</h3>
                <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-full">Optional</span>
            </div>
            <p className="text-sm text-gray-500">
                Let us know if you already have arrangements for specific days.
            </p>

            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                {getTripDays().map((date, index) => {
                    const dayNumber = index + 1;
                    const formattedDate = new Date(date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                    const lodging = dailyLodging[date] || { status: 'need', comment: '' };

                    return (
                        <div key={date} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className="font-medium text-gray-800">Day {dayNumber} • {formattedDate}</span>
                            </div>
                            
                            <div className="flex flex-col gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name={`lodging-${date}`}
                                        checked={lodging.status === 'need'}
                                        onChange={() => handleLodgingStatusChange(date, 'need')}
                                        className="text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm text-gray-700">Need recommendation</span>
                                </label>
                                
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name={`lodging-${date}`}
                                        checked={lodging.status === 'none'}
                                        onChange={() => handleLodgingStatusChange(date, 'none')}
                                        className="text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm text-gray-700">No lodging needed</span>
                                </label>

                                <div className="flex flex-col gap-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name={`lodging-${date}`}
                                            checked={lodging.status === 'has'}
                                            onChange={() => handleLodgingStatusChange(date, 'has')}
                                            className="text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm text-gray-700">I already have lodging</span>
                                    </label>
                                    
                                    {lodging.status === 'has' && (
                                        <input
                                            type="text"
                                            value={lodging.comment}
                                            onChange={(e) => handleDailyLodgingChange(date, 'has', e.target.value)}
                                            placeholder="e.g. Hotel ABC, Confirmation #123"
                                            className="ml-6 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
          </div>
      )}
    </div>
  );
};

export default StepLodging;

